import { useAuth } from '@clerk/nextjs';
import { Router } from 'next/router';
import type { PostHog } from 'posthog-js';
import { useEffect, useRef, useState } from 'react';
import { AnalyticsGroup } from '@/code/lib/analytics/events';
import { system } from '@/code/lib/system-logger';
import { trpc } from '@/code/lib/utils/trpc';

export default function PostHogProviderWrapper({
  children,
}: {
  children: React.ReactNode;
}) {
  const auth = useAuth();
  const userData = trpc.user.clerkUserData.useQuery(undefined, {
    enabled: !!auth.isSignedIn,
  });
  // No <PostHogProvider>: usePostHog() (task-detail, leads-review,
  // campaign-manager) falls back to the global singleton we init() here, so we
  // can lazy-import the SDK out of the shared bundle without breaking them.
  const posthogRef = useRef<PostHog | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    // Capture is opt-in per environment so preview/dev browsers never pollute the
    // production PostHog project. Set NEXT_PUBLIC_POSTHOG_CAPTURE_ENABLED=true only
    // in production. (Read-only employee panels are unaffected — they query server-side.)
    if (process.env.NEXT_PUBLIC_POSTHOG_CAPTURE_ENABLED !== 'true') return;

    // Never capture on the employee dashboard. It's internal-staff-only, so its
    // events carry no customer-analytics value — and worse, posthog's distinct_id
    // cookie is shared across the .aurium.ai subdomains, so a staffer's
    // employee-app session inherits whatever customer they last impersonated and
    // gets attributed to that customer's org. Gating capture here removes that
    // entire pollution vector. (We deliberately collect no product analytics on
    // internal staff — see PosthogProvider notes.)
    if (
      typeof window !== 'undefined' &&
      window.location.hostname.startsWith('employee')
    ) {
      return;
    }

    let cancelled = false;
    let stopRouter: (() => void) | undefined;

    const initPromise = import('posthog-js').then((mod) => {
      if (cancelled) return;
      // default export is the singleton; cast bridges the dynamic-import type.
      const posthog = mod.default as unknown as PostHog;
      posthog.init(process.env.NEXT_PUBLIC_POSTHOG_KEY, {
        // Same-origin relay to PostHog (src/code/routing/posthogRelay.ts). A
        // separate proxy domain doesn't survive adblockers: they uncloak its CNAME.
        api_host: '/relay',
        // Points replay/insight deep links back at PostHog, not the relay.
        ui_host:
          process.env.NEXT_PUBLIC_POSTHOG_UI_HOST || 'https://us.posthog.com',
        person_profiles: 'identified_only', // anonymous events still stitch to the person on identify(); see docs/agents/rules/posthog-analytics.md
        // We fire pageviews ourselves: the initial one from the auth-loaded effect
        // below (after the `impersonated` super-property is registered, so an
        // impersonated session's FIRST pageview is flagged), and SPA navigations
        // from the routeChangeComplete handler. Letting posthog also autocapture
        // would (a) race ahead of the impersonation flag and (b) double-count.
        capture_pageview: false,
        session_recording: {
          maskAllInputs: false,
          maskInputOptions: {
            range: false,
            select: false,
            textarea: false,
            // Mask clearly-sensitive inputs even though the rest stay visible for
            // debugging — never record raw passwords/emails/phone numbers in replays.
            password: true,
            color: false,
            text: false,
            email: true,
            number: false,
            search: false,
            tel: true,
            url: false,
            date: false,
            time: false,
            'datetime-local': false,
            month: false,
            week: false,
          },
        },
      });
      posthogRef.current = posthog;
      setIsInitialized(true);

      const handleRouteChange = () => posthog?.capture('$pageview');
      Router.events.on('routeChangeComplete', handleRouteChange);
      stopRouter = () =>
        Router.events.off('routeChangeComplete', handleRouteChange);
    });
    initPromise.catch((err) => {
      // Don't let a failed chunk load become an unhandled rejection.
      system.error('Failed to load posthog-js', err);
    });

    return () => {
      cancelled = true;
      stopRouter?.();
    };
  }, []);

  // The distinct_id we last told PostHog to identify as. Used to detect a real
  // identity SWITCH (impersonating customer A, then customer B; or account-
  // switching on a shared browser) so we can reset() before re-identifying.
  const lastIdentifiedId = useRef<string | null>(null);
  useEffect(() => {
    const posthog = posthogRef.current;
    // user may be null for employees without DB User records
    if (userData.data?.user && isInitialized && posthog?.__loaded) {
      try {
        const { user } = userData.data;
        // If a DIFFERENT user was active in this browser, reset() FIRST. reset()
        // mints a fresh distinct_id AND $session_id, so the new identity never
        // shares a session/recording with the previous one. Without this, one
        // $session_id spans multiple users (e.g. an employee impersonating A then
        // B in one sitting) and their recordings cross-attribute. First identify
        // of a normal session (ref still null) skips the reset — no spurious churn.
        if (lastIdentifiedId.current && lastIdentifiedId.current !== user.id) {
          posthog.reset();
        }
        lastIdentifiedId.current = user.id;
        posthog.identify(user.id, {
          email: user.primaryEmail,
          name: userData.data.displayName,
          organizationId: user.organizationId,
          organizationName: user.Organization?.name,
          impersonated: userData.data.impersonated,
        });
        // Tag impersonation as a SUPER property (rides every event) — reliable
        // unlike the person property above, which is set late and on the shared
        // impersonated user's profile, so it races (mixed true/false within a
        // session). Analytics excludes any session with an impersonated event.
        if (userData.data.impersonated) {
          posthog.register({ impersonated: true });
        } else {
          posthog.unregister('impersonated');
        }
        // Associate the org as a PostHog group so account-level (B2B) analytics
        // — active orgs, per-org funnels, retention — aggregate correctly.
        if (user.organizationId) {
          posthog.group(AnalyticsGroup.ORGANIZATION, user.organizationId, {
            name: user.Organization?.name,
          });
        }
      } catch (err) {
        system.error('Error identifying user in PostHog', err);
      }
    }
  }, [userData.data, isInitialized]);

  // Reset PostHog on logout so a second user on the same browser doesn't inherit
  // the previous user's identity / events. Fires only on a true→false transition.
  const wasSignedIn = useRef(false);
  useEffect(() => {
    const posthog = posthogRef.current;
    if (wasSignedIn.current && auth.isSignedIn === false && posthog?.__loaded) {
      posthog.reset();
      // Forget the identity too, so the next sign-in on this browser is treated
      // as a fresh identify rather than a switch (which would double-reset).
      lastIdentifiedId.current = null;
    }
    if (auth.isSignedIn !== undefined) {
      wasSignedIn.current = auth.isSignedIn;
    }
  }, [auth.isSignedIn]);

  // Register impersonation as early as possible: the Clerk actor claim
  // (`auth.actor`) is available client-side BEFORE the clerkUserData round-trip,
  // so even the first pageview of an impersonated session carries the super
  // property. The identify effect above also sets it (belt and suspenders), and
  // analytics excludes any session with an impersonated event.
  useEffect(() => {
    // Re-run after the SDK loads: auth.actor is usually set before posthog.
    if (!isInitialized) return;
    const posthog = posthogRef.current;
    if (posthog?.__loaded && auth.actor) {
      posthog.register({ impersonated: true });
    }
  }, [auth.actor, isInitialized]);

  // Fire the initial pageview ourselves (autocapture is off). Wait for the SDK
  // and Clerk to load so the `impersonated` super-property is registered first —
  // otherwise the very first pageview of an impersonated session would ship
  // unflagged and leak into customer analytics. Subsequent navigations come from
  // the routeChangeComplete handler.
  const firedInitialPageview = useRef(false);
  useEffect(() => {
    if (!isInitialized || !auth.isLoaded || firedInitialPageview.current) {
      return;
    }
    const posthog = posthogRef.current;
    if (!posthog?.__loaded) return;
    if (auth.actor) posthog.register({ impersonated: true });
    posthog.capture('$pageview');
    firedInitialPageview.current = true;
  }, [isInitialized, auth.isLoaded, auth.actor]);

  return <>{children}</>;
}
